import {Button, Popover, Stack, Text} from '@mantine/core'
import {DatePicker, type DatesRangeValue} from '@mantine/dates'
import {IconCalendar, IconChevronDown} from '@tabler/icons-react'
import {format} from 'date-fns'

import {toLocalDate} from './job-schedule-format'

type JobsDateFilterProps = {
    value: DatesRangeValue
    onChange: (range: DatesRangeValue) => void
}

function formatFilterDate(value: DatesRangeValue[number]) {
    if (value == null) {
        return null
    }

    return format(toLocalDate(value), 'M/d/yy')
}

function formatDateRangeLabel(range: DatesRangeValue) {
    const [start, end] = range
    const startLabel = formatFilterDate(start)
    const endLabel = formatFilterDate(end)

    if (startLabel && endLabel) {
        return startLabel === endLabel ? startLabel : `${startLabel} – ${endLabel}`
    }

    if (startLabel) {
        return `From ${startLabel}`
    }

    if (endLabel) {
        return `Until ${endLabel}`
    }

    return null
}

function dateRangeIsActive(range: DatesRangeValue) {
    return range[0] != null || range[1] != null
}

export function datesFilterButtonLabel(range: DatesRangeValue) {
    if (!dateRangeIsActive(range)) {
        return 'Any dates'
    }

    return formatDateRangeLabel(range) ?? 'Any dates'
}

export function JobsDateFilter({value, onChange}: JobsDateFilterProps) {
    return (
        <Popover width={320} position="bottom-start">
            <Popover.Target>
                <Button
                    aria-label="Dates filter"
                    variant={dateRangeIsActive(value) ? 'light' : 'default'}
                    rightSection={<IconChevronDown size={16} stroke={1.5} />}
                    leftSection={<IconCalendar size={16} stroke={1.5} />}
                >
                    {datesFilterButtonLabel(value)}
                </Button>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="sm">
                    <Text size="xs" c="dimmed">
                        Show jobs whose start or end date falls within the selected range
                    </Text>
                    <DatePicker
                        type="range"
                        allowSingleDateInRange
                        aria-label="Date range"
                        value={value}
                        onChange={onChange}
                    />
                    <Button
                        aria-label="Clear date range"
                        disabled={!dateRangeIsActive(value)}
                        variant="light"
                        size="compact-sm"
                        onClick={() => onChange([null, null])}
                    >
                        Clear
                    </Button>
                </Stack>
            </Popover.Dropdown>
        </Popover>
    )
}
